import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { alertActions } from '@/store/actions/alert.actions';

import AlertTypeEnum from '@/utils/constants/AlertTypeEnum';
import { passwordRegexp } from '@/utils/constants/regexp';

import AuthPageWrapper from '@/views/elements/AuthPageWrapper/AuthPageWrapper';
import Spinner from '@/views/components/Spinner/Spinner';
import FormInput from '@/views/components/FormInput/FormInput';
import AppAlert from '@/views/components/Alert/AppAlert';

import api from '@/services/api';

const PasswordRemind = ({ isSetPassword = false }) => {
	const [ loading, setLoading ] = useState(false);
	const [ token, setToken ] = useState(null);
	const {
		tNewPassword,
		tRepeatNewPassword,
		tPasswordInfo,
		tEnterNew,
		tEnterRepeat,
		tSetPasswordBt,
		tChangePasswordSuccess,
		tChangePasswordUnknownError,
		errors: {
			tFieldRequired: tFieldRequiredError,
			tMinChars,
			tRules,
			tMatch,
		},
		tSetPassword,
		tFieldRequired,
		tTokenExpired,
	} = useSelector(state => state.app.dict.changePassword);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const { search: locationSearch } = useLocation();

	const validationSchema = yup.object().shape({
		password: yup
			.string()
			.required(tFieldRequiredError)
			.min(8, tMinChars)
			.matches(
				passwordRegexp,
				tRules,
			),
		confirmPassword: yup
			.string()
			.required(tFieldRequiredError)
			.oneOf([ yup.ref('password'), null ], tMatch),
	});

	useEffect(() => {
		setToken(new URLSearchParams(locationSearch).get('token'));
	}, [ locationSearch ]);

	const { handleSubmit, register, formState: { errors, isValid } } = useForm({
		resolver: yupResolver(validationSchema),
		defaultValues: {
			password: '',
			confirmPassword: '',
		},
		mode: "onBlur",
	});

	const LocalInput = useCallback(({ name, label, placeholder, info }) => (
		<FormInput
			{ ...register(name) }
			labelValue={ label }
			inputType='password'
			placeholder={ placeholder }
			error={ errors[name] }
			info={ info }
		/>
	), [ register, errors ]);

	const onSubmit = async ({ password, confirmPassword }) => {
		setLoading(true);
		if (password === confirmPassword) {
			try {
				await api.post('/auth/password-reset', {
					password, confirmPassword, token,
				});
				dispatch(alertActions.setAlert(AlertTypeEnum.SUCCESS.toString(), tChangePasswordSuccess));
				navigate('/login');
			} catch (error) {
				if (error.response && error.response.status === 400) {
					dispatch(alertActions.setAlert(AlertTypeEnum.ERROR.toString(), tTokenExpired));
					navigate('/reset-password');
				} else {
					dispatch(alertActions.setAlert(AlertTypeEnum.ERROR.toString(), tChangePasswordUnknownError));
				}
			} finally {
				setLoading(false);
			}
		}
	};

	return (
		<AuthPageWrapper
			pageTitle={ tSetPassword }
			backgroundClassName='login__set-password__background--full'
			containerClassName='login__set-password__container'
			wrapperClassName='login__set-password__wrapper'
		>
			{
				loading && (
					<Spinner addClass='dictionary__spinner' />
				)
			}

			<hr />
			<form
				onSubmit={ handleSubmit(onSubmit) }
				className='login__set-password__form'
			>
				<LocalInput
					name='password'
					label={ `${tNewPassword} *` }
					placeholder={ tEnterNew }
					info={ tPasswordInfo }
				/>
				<LocalInput
					name='confirmPassword'
					label={ `${tRepeatNewPassword} *` }
					placeholder={ tEnterRepeat }
				/>
				<button
					className={ `button__blue login__form--submit${!isValid ? ' disabled' : ''}` }
					type='submit'
				>
					{tSetPasswordBt}
				</button>
				<AppAlert className={ 'login__set-password__alert' } />
				<p className={ 'login__set-password__note-required' }>{tFieldRequired}</p>
			</form>
		</AuthPageWrapper>
	);
};

PasswordRemind.propTypes = {
	isSetPassword: PropTypes.bool,
};

export default PasswordRemind;